import React, { useState, useEffect, useMemo } from 'react';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { useDispatch } from 'react-redux';
import useProgress from '../hooks/useProgress';

// Animation for thinking dots
const thinking = keyframes`
  0%  {box-shadow: 20px 0 #000, -20px 0 #0002;background: #000 }
  33% {box-shadow: 20px 0 #000, -20px 0 #0002;background: #0002}
  66% {box-shadow: 20px 0 #0002,-20px 0 #000; background: #0002}
  100%{box-shadow: 20px 0 #0002,-20px 0 #000; background: #000 }
`;
import { X, Save, AlertCircle, AlertTriangle, Edit, Eye, Trash2, History, RefreshCw, CheckCircle, XCircle, Clock, User, Flag, X as XIcon, ChevronsUp, Bot, ThumbsUp, ThumbsDown, Lightbulb } from 'lucide-react';
import { colors } from '../theme/colors';
import StatusDropdown from './StatusDropdown';
import PriorityDropdown from './PriorityDropdown';
import ModalPortal from './ModalPortal';
import { createOrUpdateOverride, getOverridesForLoan } from '../store/slices/overridesSlice';
import { useScrollLock } from '../hooks/useScrollLock';
import FieldModalHeader from './fieldModal/FieldModalHeader';
import FieldModalSidebar from './fieldModal/FieldModalSidebar';
import FieldAnalysisSection from './fieldModal/FieldAnalysisSection';
import FieldHistorySection from './fieldModal/FieldHistorySection';
import FieldDocumentResults from './fieldModal/FieldDocumentResults';
import MatchStatusIndicator from './fieldModal/MatchStatusIndicator';
import ConfidenceBar from './fieldModal/ConfidenceBar';
import FieldRerunSection from './fieldModal/FieldRerunSection';
import FieldOverridesSection from './fieldModal/FieldOverridesSection';
import { apiFetch } from '../utils/apiUtils';

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 1rem;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 12px;
  padding: 0;
  max-width: 1440px;
  width: 100%;
  height: calc(100vh - 80px);
  overflow: hidden;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  display: flex;
  flex-direction: column;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 1.5rem 2rem;
  border-bottom: 1px solid #e5e7eb;
  background: #f8fafc;
  border-radius: 12px 12px 0 0;
`;

const HeaderLeft = styled.div`
  flex: 1;
`;

const HeaderInfo = styled.div`
  display: flex;
  gap: 2rem;
  font-size: 0.875rem;
  color: #6b7280;
  margin-top: 0.5rem;
`;

const HeaderInfoItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const HeaderInfoLabel = styled.span`
  font-weight: 500;
  color: #374151;
`;

const HeaderInfoValue = styled.span`
  color: #111827;
  font-weight: 500;
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
`;

const ModalBody = styled.div`
  display: flex;
  flex: 1;
  overflow: hidden;
`;

const Sidebar = styled.div`
  width: 280px;
  background: #f8fafc;
  border-right: 1px solid #e5e7eb;
  padding: 1.5rem;
  overflow-y: auto;
`;

const MainContent = styled.div`
  flex: 1;
  padding: 1.5rem 2rem;
  overflow-y: auto;
`;

const SidebarSection = styled.div`
  margin-bottom: 2rem;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const SidebarTitle = styled.h3`
  margin: 0 0 1rem 0;
  font-size: 0.875rem;
  font-weight: 600;
  color: #374151;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const SidebarItem = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem;
  border: none;
  background: ${props => props.active ? '#eff6ff' : 'transparent'};
  border-radius: 6px;
  color: ${props => props.active ? '#1d4ed8' : '#374151'};
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  text-align: left;
  margin-bottom: 0.25rem;
  
  &:hover {
    background: ${props => props.active ? '#eff6ff' : '#f3f4f6'};
  }
`;


const StatusIndicator = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.375rem 0.75rem;
  border-radius: 16px;
  font-size: 0.75rem;
  font-weight: 500;
  background: ${props => {
    switch(props.status) {
      case 'normal': return '#f0f9ff';
      case 'client_review': return '#fef3c7';
      case 'resolved': return '#dcfce7';
      default: return '#f1f5f9';
    }
  }};
  color: ${props => {
    switch(props.status) {
      case 'normal': return '#0369a1';
      case 'client_review': return '#92400e';
      case 'resolved': return '#166534';
      default: return '#64748b';
    }
  }};
  border: 1px solid ${props => {
    switch(props.status) {
      case 'normal': return '#bae6fd';
      case 'client_review': return '#fde68a';
      case 'resolved': return '#bbf7d0';
      default: return '#e2e8f0';
    }
  }};
`;

const MatchIndicator = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.375rem 0.75rem;
  border-radius: 16px;
  font-size: 0.75rem;
  font-weight: 500;
  background: ${props => {
    switch(props.status) {
      case 'matching': return '#dcfce7';
      case 'not_matching': return '#fef2f2';
      case 'unknown': return '#f1f5f9';
      default: return '#f1f5f9';
    }
  }};
  color: ${props => {
    switch(props.status) {
      case 'matching': return '#166534';
      case 'not_matching': return '#dc2626';
      case 'unknown': return '#64748b';
      default: return '#64748b';
    }
  }};
  border: 1px solid ${props => {
    switch(props.status) {
      case 'matching': return '#bbf7d0';
      case 'not_matching': return '#fecaca';
      case 'unknown': return '#e2e8f0';
      default: return '#e2e8f0';
    }
  }};
`;

const CompactConfidence = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.5rem;
`;

const CompactConfidenceBar = styled.div`
  flex: 1;
  height: 4px;
  background: #e5e7eb;
  border-radius: 2px;
  overflow: hidden;
`;

const CompactConfidenceFill = styled.div`
  height: 100%;
  background: ${props => {
    if (props.confidence >= 0.8) return '#10b981';
    if (props.confidence >= 0.6) return '#f59e0b';
    return '#ef4444';
  }};
  width: ${props => props.confidence * 100}%;
  transition: width 0.3s ease;
`;

const CompactConfidenceText = styled.span`
  font-size: 0.75rem;
  font-weight: 500;
  color: #6b7280;
  min-width: 35px;
`;

const ActionGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem;
  margin-top: 1rem;
`;

const CompactButton = styled.button`
  padding: 0.5rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  background: white;
  color: #374151;
  font-size: 0.75rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.375rem;
  
  &:hover {
    background: #f9fafb;
    border-color: #9ca3af;
  }
  
  &.primary {
    background: ${colors.primary};
    border-color: ${colors.primary};
    color: white;
    
    &:hover {
      background: ${colors.primaryHover};
    }
  }
  
  &.warning {
    background: #f59e0b;
    border-color: #f59e0b;
    color: white;
    
    &:hover {
      background: #d97706;
    }
  }
  
  &.success {
    background: #10b981;
    border-color: #10b981;
    color: white;
    
    &:hover {
      background: #059669;
    }
  }
`;

const ContentSection = styled.div`
  margin-bottom: 2rem;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const ContentTitle = styled.h3`
  margin: 0 0 1rem 0;
  font-size: 1rem;
  font-weight: 600;
  color: #111827;
`;

const ComparisonGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  gap: 1rem;
  margin-bottom: 1.5rem;
  align-items: center;
`;

const ComparisonCard = styled.div`
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 1rem;
  box-sizing: border-box;
`;

const ComparisonTitle = styled.h4`
  margin: 0 0 0.5rem 0;
  font-size: 0.875rem;
  font-weight: 600;
  color: #374151;
`;

const ComparisonValue = styled.div`
  font-size: 0.9rem;
  color: #111827;
  font-weight: 500;
  margin-bottom: 0.25rem;
`;

const ComparisonSource = styled.div`
  font-size: 0.75rem;
  color: #6b7280;
`;


const BottomActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  padding: 1.5rem 2rem;
  border-top: 1px solid #e5e7eb;
  background: #f8fafc;
  border-radius: 0 0 12px 12px;
`;

const StatusBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.5rem;
  background: #f8fafc;
  border-bottom: 1px solid #e5e7eb;
  margin: -1.5rem -2rem 1.5rem -2rem;
`;

const StatusBarLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 1.5rem;
`;

const StatusBarRight = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const StatusGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const StatusLabel = styled.span`
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
`;

const StatusActions = styled.div`
  display: flex;
  gap: 0.5rem;
`;



const ModalTitle = styled.h2`
  margin: 0;
  color: #111827;
  font-size: 1.5rem;
  font-weight: 600;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 6px;
  color: #6b7280;
  transition: all 0.2s;
  
  &:hover {
    background: #f3f4f6;
    color: #374151;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: #374151;
  font-size: 0.9rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 0.9rem;
  transition: border-color 0.2s;
  
  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 0.9rem;
  min-height: 100px;
  resize: vertical;
  transition: border-color 0.2s;
  
  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 0.9rem;
  background: white;
  transition: border-color 0.2s;
  
  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 2rem;
  padding-top: 1rem;
  border-top: 1px solid #e5e7eb;
`;

const CancelButton = styled.button`
  padding: 0.75rem 1.5rem;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  background: white;
  color: #374151;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background: #f9fafb;
    border-color: #9ca3af;
  }
`;

const SaveButton = styled.button`
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 6px;
  background: ${colors.primary};
  color: white;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  &:hover:not(:disabled) {
    background: ${colors.primaryHover};
    transform: translateY(-1px);
    box-shadow: 0 4px 12px ${colors.primaryShadow};
  }
  
  &:disabled {
    background: #9ca3af;
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const ErrorMessage = styled.div`
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #dc2626;
  padding: 0.75rem;
  border-radius: 6px;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9rem;
`;


const FieldInfo = styled.div`
  background: #f0f9ff;
  border: 1px solid #bae6fd;
  border-radius: 6px;
  padding: 1rem;
  margin-bottom: 1.5rem;
`;

const FieldInfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const FieldInfoLabel = styled.span`
  font-weight: 500;
  color: #0369a1;
`;

const FieldInfoValue = styled.span`
  color: #0c4a6e;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-top: 1rem;
`;

const ActionButton = styled.button`
  padding: 0.5rem 1rem;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  background: white;
  color: #374151;
  font-size: 0.8rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  
  &:hover {
    background: #f9fafb;
    border-color: #9ca3af;
  }
  
  &.primary {
    background: ${colors.primary};
    border-color: ${colors.primary};
    color: white;
    
    &:hover {
      background: ${colors.primaryHover};
    }
  }
  
  &.danger {
    background: #ef4444;
    border-color: #ef4444;
    color: white;
    
    &:hover {
      background: #dc2626;
    }
  }
`;

const OverrideTypeGroup = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

const OverrideTypeLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
  background: ${props => props.checked ? '#eff6ff' : 'white'};
  border-color: ${props => props.checked ? '#3b82f6' : '#d1d5db'};
  
  &:hover {
    border-color: #3b82f6;
  }
`;

const Checkbox = styled.input`
  margin: 0;
`;




const StatusButton = styled.button`
  padding: 0.5rem 1rem;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  background: white;
  color: #374151;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  &:hover {
    background: #f9fafb;
    border-color: #9ca3af;
  }
  
  &.active {
    background: ${colors.primary};
    border-color: ${colors.primary};
    color: white;
  }
  
  &.warning {
    background: #f59e0b;
    border-color: #f59e0b;
    color: white;
    
    &:hover {
      background: #d97706;
    }
  }
  
  &.success {
    background: #10b981;
    border-color: #10b981;
    color: white;
    
    &:hover {
      background: #059669;
    }
  }
`;






const ConfidenceFill = styled.div`
  height: 100%;
  background: ${props => {
    if (props.confidence >= 0.8) return '#10b981';
    if (props.confidence >= 0.6) return '#f59e0b';
    return '#ef4444';
  }};
  width: ${props => props.confidence * 100}%;
  transition: width 0.3s ease;
`;

const MatchingActions = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-top: 1rem;
`;


function FieldModal({ 
  isOpen, 
  onClose, 
  fieldName = 'Borrower_Name',
  fieldUuid = null,  // PRIMARY identifier for API calls
  currentValues = {
    value: 'Acme 777 Unlimited Corporation',
    master_value: 'Acme 777 Unlimited Corporation',
    source: 'PROPOSAL'
  },
  onSave,
  loading = false,
  activeSection: initialActiveSection = 'matching',
  historyData = [],
  loanId = '1234567890',
  borrowerName = 'Acme 777 Unlimited Corporation',
  onRerunFieldExtraction = null
}) {
  const dispatch = useDispatch();
  
  // WebSocket progress for field extraction
  const { 
    progress, 
    status, 
    isActive: isProgressActive,
    resetProgress
  } = useProgress();
  
  // Lock body scroll when modal is open
  useScrollLock(isOpen);
  
  // Reset progress bar and state when modal opens
  useEffect(() => {
    if (isOpen) {
      resetProgress();
      // Reset all editing state to avoid stale data
      setReplacedValue(null);
      setReplacedSource(null);
      setSelectedMatchType(null);
      setManualMatchStatus(currentValues?.match_status_override || null);
      setShowUndo(false);
      setEditedExtractedValue('');
      setEditedCoreValue('');
      setShowDocumentResults(false);
    }
  }, [isOpen, resetProgress, currentValues?.match_status_override]);
  
  const [overrideTypes, setOverrideTypes] = useState({
    value: false,
    master_value: false,
    source: false
  });
  const [overrideValues, setOverrideValues] = useState({
    value: '',
    master_value: '',
    source: ''
  });
  const [comment, setComment] = useState('');
  const [createdBy, setCreatedBy] = useState('Gary');
  const [error, setError] = useState('');
  
  // Field management state
  const [fieldStatus, setFieldStatus] = useState('none'); // none, client_review, resolved, ignore
  const [fieldPriority, setFieldPriority] = useState('critical'); // critical, high_exception, conditional
  
  // Document value replacement state
  const [hoveredDocument, setHoveredDocument] = useState(null);
  const [replacedValue, setReplacedValue] = useState(null);
  const [replacedSource, setReplacedSource] = useState(null);
  const [selectedMatchType, setSelectedMatchType] = useState(null); // Store match type from selected document
  const [showUndo, setShowUndo] = useState(false);
  
  // Manual match status state - initialize with existing override if available
  const [manualMatchStatus, setManualMatchStatus] = useState(
    currentValues?.match_status_override || null
  ); // null = auto, 'perfect' | 'matched' | 'maybe' | 'mismatch'
  
  // Analysis state
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [analysisError, setAnalysisError] = useState('');
  
  // Update manual match status when currentValues change (e.g., when modal opens with different field)
  useEffect(() => {
    setManualMatchStatus(currentValues?.match_status_override || null);
    // Reset analysis state when field changes
    setAnalysisResult(null);
    setAnalysisError('');
    setIsAnalyzing(false);
    
    // BUG FIX: Reset modal to Matching tab when field changes (always open to Matching tab)
    setActiveSection('matching');
    
    // BUG FIX: Load existing flag override or reset to 'none' if no override exists
    if (currentValues?.flag_override) {
      setFieldStatus(currentValues.flag_override);
    } else {
      setFieldStatus('none');
    }
    
    // Reset comment (will be loaded from override if exists)
    setComment('');
    
    // Reset document replacement state
    setReplacedValue(null);
    setReplacedSource(null);
    setSelectedMatchType(null);
    setShowUndo(false);
    
    // Reset history state
    setHistoryDataState([]);
    setLoadingHistory(false);
  }, [currentValues?.match_status_override, currentValues?.flag_override, fieldName]);
  
  // Calculate match status and confidence based on values
  const matchData = useMemo(() => {
    if (!currentValues) return { status: 'unknown', confidence: 0 };
    
    const extracted = (replacedValue || currentValues.value)?.toLowerCase().trim();
    const core = currentValues.master_value?.toLowerCase().trim();
    
    // Match calculation logic
    
    if (!extracted || !core) return { status: 'unknown', confidence: 0 };
    
    if (extracted === core) {
      // Perfect match detected
      return { status: 'matching', confidence: 1.0 };
    }
    
    // Check for partial matches (addresses, names with variations)
    const extractedWords = extracted.split(/\s+/);
    const coreWords = core.split(/\s+/);
    const commonWords = extractedWords.filter(word => coreWords.includes(word));
    const similarity = commonWords.length / Math.max(extractedWords.length, coreWords.length);
    
    // Similarity calculation
    
    if (similarity >= 0.8) return { status: 'matching', confidence: 0.85 };
    if (similarity >= 0.5) return { status: 'not_matching', confidence: 0.6 };
    return { status: 'not_matching', confidence: 0.2 };
  }, [replacedValue, currentValues?.value, currentValues?.master_value]);
  
  // Calculate match type by comparing extracted value against core value
  const calculateMatchType = (extractedValue, coreValue) => {
    if (!extractedValue || !coreValue) return 'none';
    
    // Normalize values for comparison
    const extracted = extractedValue.toString().trim().toLowerCase();
    const core = coreValue.toString().trim().toLowerCase();
    
    // Perfect match
    if (extracted === core) return 'perfect';
    
    // For rate fields, handle rate structure lookups
    if (fieldName.toLowerCase().includes('rate')) {
      // Rate structure lookup - map common rate structures to their equivalent values
      const rateStructureLookup = {
        '0.0725': [
          'wall street journal prime rate',
          'wsj prime rate', 
          'prime rate'
        ],
        '0.0875': [
          'prime plus 1.5%',
          'prime + 1.5%',
          'prime plus 150 basis points',
          'prime + 150 basis points',
          '150 basis points'
        ],
        '0.065': ['6.5%', '6.50%'],
        '0.0525': ['5.25%', '5.25'],
        '0.0175': ['1.75%', '1.75'],
        '0.09': [
          'prime plus 1.75%',
          'prime + 1.75%',
          'prime plus 175 basis points',
          'prime + 175 basis points'
        ],
        // Add more mappings as needed
      };
      
      // Check if extracted value matches a rate structure for the core value
      for (const [coreRate, structures] of Object.entries(rateStructureLookup)) {
        if (core === coreRate.toLowerCase()) {
          for (const structure of structures) {
            if (extracted.includes(structure)) {
              return 'perfect';
            }
          }
        }
      }
      
      // Explicit check for rate structures that should NOT match the core value
      if (core === '0.0725') {
        // These rate structures are NOT the same as 7.25% prime rate
        const nonMatchingStructures = [
          'prime plus 150 basis points',
          'prime + 150 basis points',
          '150 basis points',
          'prime plus 1.5%',
          'prime + 1.5%',
          'prime plus 1.75%',
          'prime + 1.75%',
          '1.75% over',
          '6.5%',
          '6.50%'
        ];
        
        for (const structure of nonMatchingStructures) {
          if (extracted.includes(structure)) {
            return 'none'; // Explicit no match for these structures
          }
        }
      }
      
      // Try to extract numeric values for comparison
      const extractNumeric = (value) => {
        // Extract decimal numbers from the value
        const match = value.match(/(\d+\.?\d*)/);
        return match ? parseFloat(match[1]) : null;
      };
      
      const extractedNum = extractNumeric(extracted);
      const coreNum = extractNumeric(core);
      
      if (extractedNum !== null && coreNum !== null) {
        // Normalize both values to the same unit (decimal form)
        let normalizedExtracted = extractedNum;
        let normalizedCore = coreNum;
        
        // If extracted value contains '%' or is > 1, treat as percentage and convert to decimal
        if (extracted.includes('%') || extractedNum > 1) {
          normalizedExtracted = extractedNum / 100;
        }
        
        // If core value is > 1, treat as percentage and convert to decimal
        if (coreNum > 1) {
          normalizedCore = coreNum / 100;
        }
        
        // Check if they're close (within 0.001 for rates in decimal form)
        const diff = Math.abs(normalizedExtracted - normalizedCore);
        if (diff < 0.001) return 'perfect';
        if (diff < 0.01) return 'partial';
      }
    }
    
    // Check for partial matches (contains core value or vice versa)
    if (extracted.includes(core) || core.includes(extracted)) return 'partial';
    
    return 'none';
  };

  // Determine the display match status (manual override or auto-calculated)
  const getDisplayMatchStatus = () => {
    // If we have a manual override, use it (except perfect matches can't be overridden)
    if (manualMatchStatus) {
      return manualMatchStatus;
    }
    
    // Return default if currentValues is not available yet
    if (!currentValues) return 'maybe';
    
    // Check for "No Data" scenario FIRST: both values are "Not Found"
    // This takes precedence over system_document_match to handle the specific case
    const extracted = (replacedValue || currentValues?.value)?.toLowerCase().trim();
    const core = currentValues?.master_value?.toLowerCase().trim();
    
    if (extracted === 'not found' && core === 'not found') {
      return 'no_data';
    }
    
    // If user has selected a replaced value, use the matchType from the selected document
    if (replacedValue) {
      // If we have the matchType from the backend matching service, use it!
      if (selectedMatchType) {
        // Map backend matchType to display status
        switch(selectedMatchType) {
          case 'perfect':
            return 'perfect';
          case 'partial':
            return 'maybe';
          case 'none':
            return 'mismatch';
          default:
            break;
        }
      }
      
      // Fallback: recalculate based on string comparison
      if (!extracted || !core) return 'maybe';
      
      // Perfect match
      if (extracted === core) return 'perfect';
      
      // Check for partial matches
      const extractedWords = extracted.split(/\s+/);
      const coreWords = core.split(/\s+/);
      const commonWords = extractedWords.filter(word => coreWords.includes(word));
      const similarity = commonWords.length / Math.max(extractedWords.length, coreWords.length);
      
      if (similarity >= 0.8) return 'maybe';
      return 'mismatch';
    }
    
    // Use the system_document_match value from the database if available (only if no replacedValue)
    if (currentValues.system_document_match) {
      switch (currentValues.system_document_match) {
        case 'HIGH':
        case 'PERFECT_MATCH':
          return 'perfect';
        case 'MEDIUM':
        case 'MAYBE_MATCH':
          return 'maybe';
        case 'LOW':
          return 'mismatch';
        case 'NO_MATCH':
          return 'mismatch';
        default:
          break;
      }
    }
    
    // Fallback: Auto-calculate based on values if no system_document_match
    if (!extracted || !core) return 'maybe';
    
    // Perfect match
    if (extracted === core) return 'perfect';
    
    // Check for partial matches
    const extractedWords = extracted.split(/\s+/);
    const coreWords = core.split(/\s+/);
    const commonWords = extractedWords.filter(word => coreWords.includes(word));
    const similarity = commonWords.length / Math.max(extractedWords.length, coreWords.length);
    
    if (similarity >= 0.8) return 'maybe';
    return 'mismatch';
  };
  
  const displayMatchStatus = useMemo(() => getDisplayMatchStatus(), [manualMatchStatus, currentValues, replacedValue, selectedMatchType]);
  const isPerfectMatch = displayMatchStatus === 'perfect';
  
  const matchStatus = matchData.status;
  const confidence = matchData.confidence;
  const [lastProcessed, setLastProcessed] = useState('2024-01-15T10:30:00Z');
  const [isProcessing, setIsProcessing] = useState(false);
  const [activeSection, setActiveSection] = useState(initialActiveSection); // matching, overrides, rerun, history
  const [historyDataState, setHistoryDataState] = useState(historyData || []);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [showDocumentResults, setShowDocumentResults] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [showDetailedView, setShowDetailedView] = useState(false);
  const [detailedDocumentIndex, setDetailedDocumentIndex] = useState(null);

  // Auto-run analysis when analysis section becomes active
  useEffect(() => {
    if (activeSection === 'analysis' && !analysisResult && !isAnalyzing && !analysisError) {
      handleAnalyze();
    }
    
    // Fetch history when history tab is clicked - always fetch to get latest
    if (activeSection === 'history' && !loadingHistory) {
      fetchFieldHistory();
    }
  }, [activeSection]);
  
  const fetchFieldHistory = async () => {
    setLoadingHistory(true);
    try {
      // Use field_uuid if available, otherwise fall back to field_name
      const fieldIdentifier = fieldUuid || encodeURIComponent(fieldName);
      const url = `${import.meta.env.VITE_API_URL || 'http://127.0.0.1:8000/api'}/activity/${loanId}/${fieldIdentifier}`;
      
      const response = await fetch(url, {
        headers: {
          ...(window.Cypress ? {
            'X-Test-Mode': 'true',
            'X-Test-DB-Name': 'ocr_matching_test',
            'X-Test-User': 'gary'
          } : {}),
          // Include auth token from localStorage (use correct key: 'auth_token')
          'Authorization': `Bearer ${localStorage.getItem('auth_token') || ''}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        
        // Transform API data to match FieldHistorySection format
        const formattedHistory = data.map(entry => ({
          type: entry.activity_type,
          title: formatActivityTitle(entry),
          description: formatActivityDescription(entry),
          timestamp: new Date(entry.created_at).toLocaleString(),
          user: entry.created_by_username || 'Unknown User',
          changes: entry.old_value && entry.new_value ? {
            value: { from: entry.old_value, to: entry.new_value }
          } : null,
          comment: entry.comment
        }));
        
        setHistoryDataState(formattedHistory);
      }
    } catch (error) {
      console.error('[FieldModal] Error fetching history:', error);
    } finally {
      setLoadingHistory(false);
    }
  };
  
  const formatActivityTitle = (entry) => {
    switch(entry.activity_type) {
      case 'override_created':
        return 'Override Created';
      case 'override_updated':
        return 'Override Updated';
      case 'match_status_changed':
        return 'Match Status Changed';
      case 'comment_added':
        return 'Comment Added';
      case 'flag_changed':
        return 'Flag Changed';
      default:
        return entry.activity_type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    }
  };
  
  const formatActivityDescription = (entry) => {
    if (entry.old_value && entry.new_value) {
      return `Changed from "${entry.old_value}" to "${entry.new_value}"`;
    } else if (entry.new_value) {
      return `Set to "${entry.new_value}"`;
    }
    return entry.comment || '';
  };
  
  // Inline editing state
  const [isEditingCoreValue, setIsEditingCoreValue] = useState(false);
  const [editedCoreValue, setEditedCoreValue] = useState(currentValues?.master_value || '');
  const [isEditingExtractedValue, setIsEditingExtractedValue] = useState(false);
  const [editedExtractedValue, setEditedExtractedValue] = useState(currentValues?.value || '');
  const [hasOverrides, setHasOverrides] = useState(false);

  // Document results data - now as state
  const [documentResults, setDocumentResults] = useState([
    {
      document: 'Note',
      page: 1,
      status: 'found',
      value: 'Demand',
      confidence: 0.95,
      matchType: 'partial',
      color: '#4A69E0',
      priority: '1st'
    },
    {
      document: 'Proposal',
      page: 1,
      status: 'found',
      value: '05-07-2059',
      confidence: 0.98,
      matchType: 'perfect',
      color: '#3D8B5F',
      priority: '2nd'
    },
    {
      document: 'Service Agreement',
      page: 1,
      status: 'found',
      value: 'Demand',
      confidence: 0.85,
      matchType: 'partial',
      color: '#D9772B',
      priority: '3rd'
    },
    {
      document: 'Additional',
      page: 1,
      status: 'not_found',
      value: null,
      confidence: 0,
      matchType: 'none',
      color: '#8B4AD9',
      priority: '4th'
    }
  ]);

  // History data - will be passed as props in stories
  // (already destructured from props above)

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setOverrideTypes({ value: false, master_value: false, source: false });
      setOverrideValues({ value: '', master_value: '', source: '' });
      setComment('');
      setCreatedBy('Gary');
      setError('');
      
      // Clear field extraction results to prevent stale state
      setDocumentResults([]);
      setShowDocumentResults(false);
      setSelectedDocument(null);
      setShowDetailedView(false);
      setDetailedDocumentIndex(null);
    }
  }, [isOpen]);

  // Clear field extraction results when field name changes
  useEffect(() => {
    setDocumentResults([]);
    setShowDocumentResults(false);
    setSelectedDocument(null);
    setShowDetailedView(false);
    setDetailedDocumentIndex(null);
  }, [fieldName]);

  // Cleanup on unmount to prevent stale state
  useEffect(() => {
    return () => {
      // Cleanup function runs when component unmounts
      setDocumentResults([]);
      setShowDocumentResults(false);
      setSelectedDocument(null);
      setShowDetailedView(false);
      setDetailedDocumentIndex(null);
    };
  }, []);

  const handleOverrideTypeChange = (type) => {
    setOverrideTypes(prev => ({
      ...prev,
      [type]: !prev[type]
    }));
    
    // Clear the value when unchecking
    if (overrideTypes[type]) {
      setOverrideValues(prev => ({
        ...prev,
        [type]: ''
      }));
    }
  };

  const handleValueChange = (type, value) => {
    setOverrideValues(prev => ({
      ...prev,
      [type]: value
    }));
  };

  const handleSave = async () => {
    setError('');
    
    try {
      let needsRefresh = false; // Track if we need to refresh overrides
      
      // Save match status override if it changed
      if (manualMatchStatus && manualMatchStatus !== currentValues?.match_status_override) {
        await dispatch(createOrUpdateOverride({
          loanId: loanId,
          fieldName: fieldName,
          fieldUuid: fieldUuid, // Include UUID for proper identification
          overrideType: 'match_status',
          overriddenValue: manualMatchStatus,
          overriddenKey: null,
          comment: comment || `Match status manually set to ${manualMatchStatus}`,
          createdBy: 'Current User' // TODO: Get from auth context
        })).unwrap();
        needsRefresh = true;
      }
      
      // Handle flag (fieldStatus) changes
      if (fieldStatus !== currentValues?.flag_override) {
        if (fieldStatus && fieldStatus !== 'none') {
          // Save new flag
          await dispatch(createOrUpdateOverride({
            loanId: loanId,
            fieldName: fieldName,
            fieldUuid: fieldUuid, // Include UUID for proper identification
            overrideType: 'flag',
            overriddenValue: fieldStatus,
            overriddenKey: null,
            comment: comment || `Flag set to ${fieldStatus}`,
            createdBy: 'Current User' // TODO: Get from auth context
          })).unwrap();
          console.log(`✅ Flag saved: ${fieldStatus}`);
        } else if (fieldStatus === 'none' && currentValues?.flag_override) {
          // Remove existing flag override
          await dispatch(createOrUpdateOverride({
            loanId: loanId,
            fieldName: fieldName,
            fieldUuid: fieldUuid, // Include UUID for proper identification
            overrideType: 'flag',
            overriddenValue: null, // Set to null to remove
            overriddenKey: null,
            comment: comment || `Flag removed (set to none)`,
            createdBy: 'Current User' // TODO: Get from auth context
          })).unwrap();
          console.log(`✅ Flag removed (set to none)`);
        }
        needsRefresh = true;
      }
      
      // Save replaced value from document selection if it exists
      if (replacedValue && replacedValue !== currentValues?.value) {
        // Save the value override
        await dispatch(createOrUpdateOverride({
          loanId: loanId,
          fieldName: fieldName,
          fieldUuid: fieldUuid, // Include UUID for proper identification
          overrideType: 'value',
          overriddenValue: replacedValue,
          overriddenKey: replacedSource || null,
          comment: comment || `Value replaced with "${replacedValue}" from ${replacedSource || 'document'}`,
          createdBy: 'Current User' // TODO: Get from auth context
        })).unwrap();
        console.log(`✅ Replaced value override saved: ${replacedValue} from ${replacedSource}`);
        
        // Save match_status based on the selectedMatchType from backend matching service
        if (selectedMatchType) {
          // Map backend matchType to display status for saving
          let matchStatusToSave;
          switch(selectedMatchType) {
            case 'perfect':
              matchStatusToSave = 'perfect';
              break;
            case 'partial':
              matchStatusToSave = 'maybe';
              break;
            case 'none':
              matchStatusToSave = 'mismatch';
              break;
            default:
              matchStatusToSave = null;
          }
          
          if (matchStatusToSave) {
            await dispatch(createOrUpdateOverride({
              loanId: loanId,
              fieldName: fieldName,
              fieldUuid: fieldUuid,
              overrideType: 'match_status',
              overriddenValue: matchStatusToSave,
              overriddenKey: null,
              comment: comment || `Match status set to ${matchStatusToSave} (from backend matching)`,
              createdBy: 'Current User'
            })).unwrap();
            console.log(`✅ Match status override saved: ${matchStatusToSave} (from selectedMatchType: ${selectedMatchType})`);
          }
        } else {
          console.log(`⚠️ Match status NOT saved - no selectedMatchType from backend`);
        }
        
        needsRefresh = true;
      }
      // Otherwise, save edited extracted value if it changed
      else if (editedExtractedValue && editedExtractedValue !== currentValues?.value) {
        await dispatch(createOrUpdateOverride({
          loanId: loanId,
          fieldName: fieldName,
          fieldUuid: fieldUuid, // Include UUID for proper identification
          overrideType: 'value',
          overriddenValue: editedExtractedValue,
          overriddenKey: null,
          comment: comment || `Value manually changed to ${editedExtractedValue}`,
          createdBy: 'Current User' // TODO: Get from auth context
        })).unwrap();
        console.log(`✅ Extracted value override saved: ${editedExtractedValue}`);
        needsRefresh = true;
      }
      
      // Save edited core value if it changed
      if (editedCoreValue && editedCoreValue !== currentValues?.master_value) {
        await dispatch(createOrUpdateOverride({
          loanId: loanId,
          fieldName: fieldName,
          fieldUuid: fieldUuid, // Include UUID for proper identification
          overrideType: 'master_value',
          overriddenValue: editedCoreValue,
          overriddenKey: null,
          comment: comment || `Core value manually changed to ${editedCoreValue}`,
          createdBy: 'Current User' // TODO: Get from auth context
        })).unwrap();
        console.log(`✅ Core value override saved: ${editedCoreValue}`);
        needsRefresh = true;
      }
      
      // Save other overrides if any are selected
      const selectedTypes = Object.entries(overrideTypes).filter(([_, selected]) => selected);
      if (selectedTypes.length > 0) {
        // Validate that values are provided for selected types
        for (const [type] of selectedTypes) {
          if (!overrideValues[type].trim()) {
            setError(`Please provide a value for ${type.replace('_', ' ')}.`);
            return;
          }
        }
        
        await onSave({
          fieldName,
          overrideTypes: selectedTypes.map(([type]) => type),
          overrideValues,
          comment,
          createdBy
        });
        needsRefresh = true;
      }
      
      // If ONLY a comment was added (no other changes), save it as a standalone comment
      if (!needsRefresh && comment && comment.trim()) {
        await dispatch(createOrUpdateOverride({
          loanId: loanId,
          fieldName: fieldName,
          fieldUuid: fieldUuid, // Include UUID for proper identification
          overrideType: 'comment',
          overriddenValue: comment.trim(),
          overriddenKey: null,
          comment: comment.trim(),
          createdBy: 'Current User'
        })).unwrap();
        needsRefresh = true;
        console.log('✅ Standalone comment saved');
      }
      
      // Refresh overrides once at the end if any were saved
      if (needsRefresh) {
        await dispatch(getOverridesForLoan({ loanId, source: 'FieldModal.jsx:handleSave' })).unwrap();
        
        // Also refresh the field data to update flag_override properties
        window.dispatchEvent(new CustomEvent('refreshLoanData', { 
          detail: { loanId, forceRefresh: true } 
        }));
      }
      
      // Close modal after all saves complete
      setHasOverrides(false);
      onClose();
    } catch (err) {
      console.error('[handleSave] Error during save:', err);
      setError(err.message || 'Failed to save overrides.');
    }
  };

  const handleStatusChange = (newStatus) => {
    // Handle both direct value and event object formats
    const statusValue = newStatus?.target?.value || newStatus;
    setFieldStatus(statusValue);
    setHasOverrides(true); // Enable save button when flag changes
  };

  const handleReRunMatching = async () => {
    setIsProcessing(true);
    setError('');
    
    try {
      console.log(`🔄 Re-running field extraction for ${fieldName} in loan ${loanId}`);
      
      // Use passed callback if available, otherwise use direct API call
      if (onRerunFieldExtraction) {
        const result = await onRerunFieldExtraction(loanId, fieldName);
        
        if (result.success) {
          console.log(`✅ Successfully extracted ${fieldName}:`, result.document_results);
          
          // Update document results to display in the modal
          setDocumentResults(result.document_results.map(doc => ({
            document: doc.document.toUpperCase(), // Convert to uppercase for display
            value: doc.value,
            textValue: doc.textValue || doc.value,
            numValue: doc.numValue,
            confidence: doc.confidence,
            page: doc.page,
            source_context: doc.source_context,
            status: doc.found ? 'found' : 'not_found',
            matchType: doc.matchType || 'none',
            matchSource: doc.matchSource || 'unknown',
            matchReasoning: doc.matchReasoning || '',
            normalization: doc.normalization || null
          })));
          
          // Show document results section
          setShowDocumentResults(true);
          
          // Update last processed timestamp
          setLastProcessed(new Date().toISOString());
          
          console.log(`📊 Document results updated and displayed`);
        } else {
          throw new Error(result.error || 'Field extraction failed');
        }
      } else {
        // Fallback to direct API call
        const response = await apiFetch(`/extraction/rerun-field/${loanId}/${encodeURIComponent(fieldName)}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            // Include X-Test-Mode header in Cypress tests
            ...(window.Cypress ? { 'X-Test-Mode': 'true' } : {})
          }
        });
        
        // 🔍 DEBUG: Check what we got from apiFetch
        console.log('🔍 [FIELD MODAL DEBUG] Response from apiFetch:', response);
        console.log('🔍 [FIELD MODAL DEBUG] Response type:', typeof response);
        console.log('🔍 [FIELD MODAL DEBUG] Response keys:', Object.keys(response));
        console.log('🔍 [FIELD MODAL DEBUG] Response.success:', response.success);
        console.log('🔍 [FIELD MODAL DEBUG] Response.ok:', response.ok);
        console.log('🔍 [FIELD MODAL DEBUG] Does response have .json method?', typeof response.json);
        
        const result = response;
        
        if (result.success) {
          console.log(`✅ Successfully extracted ${fieldName}:`, result.document_results);
          
          // Update document results to display in the modal
          setDocumentResults(result.document_results.map(doc => ({
            document: doc.document.toUpperCase(), // Convert to uppercase for display
            value: doc.value,
            textValue: doc.textValue || doc.value,
            numValue: doc.numValue,
            confidence: doc.confidence,
            page: doc.page,
            source_context: doc.source_context,
            status: doc.found ? 'found' : 'not_found',
            matchType: doc.matchType || 'none',
            matchSource: doc.matchSource || 'unknown',
            matchReasoning: doc.matchReasoning || '',
            normalization: doc.normalization || null
          })));
          
          // Show document results section
          setShowDocumentResults(true);
          
          // Update last processed timestamp
          setLastProcessed(new Date().toISOString());
          
          console.log(`📊 Document results updated and displayed`);
        } else {
          throw new Error(result.error || 'Field extraction failed');
        }
      }
      
    } catch (err) {
      console.error('Error in field extraction:', err);
      setError(err.message || 'Failed to extract field from documents');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleManualMatchStatus = (newMatchStatus) => {
    setMatchStatus(newMatchStatus);
  };
  
  const handleMatchStatusChange = (newStatus) => {
    // Just update local state - don't save until user clicks Save button
    console.log(`[handleMatchStatusChange] OLD: ${manualMatchStatus} → NEW: ${newStatus}`);
    setManualMatchStatus(newStatus);
    setHasOverrides(true); // Enable Save button
    console.log(`Match status changed to: ${newStatus} (not saved yet)`);
  };
  
  const renderMatchIndicator = () => {
    return (
      <MatchStatusIndicator 
        status={displayMatchStatus}
        isPerfectMatch={isPerfectMatch}
        onStatusChange={handleMatchStatusChange}
      />
    );
  };

  const getStatusIcon = (status) => {
    switch(status) {
      case 'normal': return <CheckCircle size={16} />;
      case 'client_review': return <Flag size={16} />;
      case 'resolved': return <CheckCircle size={16} />;
      default: return <Clock size={16} />;
    }
  };

  const getMatchIcon = (status) => {
    switch(status) {
      case 'matching': return <CheckCircle size={16} />;
      case 'not_matching': return <XCircle size={16} />;
      case 'unknown': return <Clock size={16} />;
      default: return <Clock size={16} />;
    }
  };

  if (!isOpen || !currentValues) return null;

  const renderSidebar = () => (
    <FieldModalSidebar
      activeSection={activeSection}
      onSectionChange={setActiveSection}
      displayMatchStatus={displayMatchStatus}
    />
  );

  const renderStatusBar = () => (
    <StatusBar>
      <StatusBarLeft>
        <StatusGroup>
          <StatusLabel>Flag:</StatusLabel>
          <StatusDropdown 
            value={fieldStatus} 
            onChange={handleStatusChange}
          />
        </StatusGroup>
      </StatusBarLeft>
      <StatusBarRight>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          {renderConfidenceBar()}
        </div>
      </StatusBarRight>
    </StatusBar>
  );

  const renderCompactStatus = () => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '2rem', width: '100%', justifyContent: 'space-between' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        <span style={{ fontSize: '0.875rem', fontWeight: '500', color: '#374151' }}>Flag:</span>
        <StatusDropdown 
          value={fieldStatus} 
          onChange={handleStatusChange}
        />
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        <span style={{ fontSize: '0.875rem', fontWeight: '500', color: '#374151' }}>Match:</span>
        {renderConfidenceBar()}
      </div>
    </div>
  );

  const renderConfidenceBar = () => {
    if (!currentValues) return null;
    
    const currentValue = replacedValue || currentValues.value;
    const coreValue = currentValues.master_value;
    
    return (
      <ConfidenceBar 
        currentValue={currentValue}
        coreValue={coreValue}
      />
    );
  };


  const handleAnalyze = async () => {
    setIsAnalyzing(true);
    setAnalysisError('');
    
    try {
      const data = await apiFetch('/analysis/field-analysis', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          field_name: fieldName,
          loan_type: 'commercial',
          document_value: currentValues.value,
          system_value: currentValues.master_value,
          match_status: displayMatchStatus
        }),
      });

      // apiFetch already handles error checking and returns parsed JSON
      setAnalysisResult(data.analysis);
    } catch (err) {
      setAnalysisError(err.message);
    } finally {
      setIsAnalyzing(false);
    }
  };


  const renderMainContent = () => {
    switch(activeSection) {
      case 'rerun':
        return (
          <FieldRerunSection
            currentValues={currentValues}
            lastProcessed={lastProcessed}
            isProcessing={isProcessing}
            onReRun={handleReRunMatching}
            renderStatusBar={renderStatusBar}
          />
        );

      case 'matching':
        return (
          <>
            {renderStatusBar()}
            <ContentSection>
              {showUndo ? (
                <div style={{
                  background: '#f0f9ff',
                  border: '1px solid #0ea5e9',
                  borderRadius: '6px',
                  padding: '0.75rem',
                  marginBottom: '1rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <CheckCircle size={16} style={{ color: '#0ea5e9' }} />
                    <span style={{ fontSize: '0.875rem', color: '#0c4a6e' }}>
                      Value replaced with "{replacedValue}" from {replacedSource}
                    </span>
                  </div>
                  <button
                    onClick={() => {
                      setReplacedValue(null);
                      setReplacedSource(null);
                      setSelectedMatchType(null);
                      setShowUndo(false);
                      setHasOverrides(false);
                    }}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#0ea5e9',
                      cursor: 'pointer',
                      fontSize: '0.875rem',
                      fontWeight: '500',
                      textDecoration: 'underline'
                    }}
                  >
                    Undo
                  </button>
                </div>
              ) : (
                <ContentTitle>Value Comparison</ContentTitle>
              )}
              <ComparisonGrid>
                <ComparisonCard style={{ 
                  opacity: showDocumentResults && !replacedValue && !hoveredDocument ? 0.6 : 1,
                  border: '1px solid #e5e7eb',
                  boxShadow: hoveredDocument !== null ? '0 0 0 2px #007bff' : 'none',
                  transition: 'all 0.2s ease'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                    <ComparisonTitle>Original Value</ComparisonTitle>
                    {!isEditingExtractedValue && (
                      <button
                        data-cy="edit-original-value-btn"
                        onClick={() => setIsEditingExtractedValue(true)}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: '#6b7280',
                          cursor: 'pointer',
                          padding: '0.25rem',
                          borderRadius: '4px',
                          display: 'flex',
                          alignItems: 'center',
                          transition: 'all 0.2s'
                        }}
                        onMouseOver={(e) => {
                          e.target.style.color = '#3b82f6';
                          e.target.style.background = '#f3f4f6';
                        }}
                        onMouseOut={(e) => {
                          e.target.style.color = '#6b7280';
                          e.target.style.background = 'none';
                        }}
                        title="Edit extracted value"
                      >
                        <Edit size={14} />
                      </button>
                    )}
                  </div>
                  {isEditingExtractedValue ? (
                    <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                      <input
                        data-cy="original-value-input"
                        type="text"
                        value={editedExtractedValue}
                        onChange={(e) => setEditedExtractedValue(e.target.value)}
                        style={{
                          flex: 1,
                          padding: '0.5rem',
                          border: '1px solid #3b82f6',
                          borderRadius: '4px',
                          fontSize: '0.875rem',
                          fontFamily: 'inherit',
                          background: 'white'
                        }}
                        autoFocus
                      />
                      <button
                        data-cy="save-original-value-btn"
                        onClick={() => {
                          // Apply the override
                          setIsEditingExtractedValue(false);
                          setHasOverrides(true);
                          // Here you would typically call an API to save the override
                        }}
                        style={{
                          background: colors.primary,
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          padding: '0.5rem',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          transition: 'background 0.2s'
                        }}
                        onMouseOver={(e) => e.target.style.background = colors.primaryHover}
                        onMouseOut={(e) => e.target.style.background = colors.primary}
                        title="Save override"
                      >
                        <Save size={14} />
                      </button>
                      <button
                        data-cy="cancel-original-value-btn"
                        onClick={() => {
                          setEditedExtractedValue(currentValues.value);
                          setIsEditingExtractedValue(false);
                        }}
                        style={{
                          background: '#6b7280',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          padding: '0.5rem',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          transition: 'background 0.2s'
                        }}
                        onMouseOver={(e) => e.target.style.background = '#4b5563'}
                        onMouseOut={(e) => e.target.style.background = '#6b7280'}
                        title="Cancel edit"
                      >
                        <XIcon size={14} />
                      </button>
                    </div>
                  ) : (
                    <ComparisonValue>
                      {hoveredDocument !== null && documentResults[hoveredDocument]?.status === 'found' 
                        ? documentResults[hoveredDocument].value 
                        : replacedValue || currentValues.value}
                    </ComparisonValue>
                  )}
                  <ComparisonSource>
                    Source: {hoveredDocument !== null && documentResults[hoveredDocument]?.status === 'found' 
                      ? documentResults[hoveredDocument].document 
                      : replacedSource || currentValues.source}
                  </ComparisonSource>
                </ComparisonCard>
                
                {/* Match Status Indicator */}
                {renderMatchIndicator()}
                
              <ComparisonCard>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                  <ComparisonTitle>Core Value</ComparisonTitle>
                  {!isEditingCoreValue && (
                    <button
                      data-cy="edit-core-value-btn"
                      onClick={() => setIsEditingCoreValue(true)}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: '#6b7280',
                        cursor: 'pointer',
                        padding: '0.25rem',
                        borderRadius: '4px',
                        display: 'flex',
                        alignItems: 'center',
                        transition: 'all 0.2s'
                      }}
                      onMouseOver={(e) => {
                        e.target.style.color = '#3b82f6';
                        e.target.style.background = '#f3f4f6';
                      }}
                      onMouseOut={(e) => {
                        e.target.style.color = '#6b7280';
                        e.target.style.background = 'none';
                      }}
                      title="Edit core value"
                    >
                      <Edit size={14} />
                    </button>
                  )}
                </div>
                {isEditingCoreValue ? (
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                    <input
                      data-cy="core-value-input"
                      type="text"
                      value={editedCoreValue}
                      onChange={(e) => setEditedCoreValue(e.target.value)}
                      style={{
                        flex: 1,
                        padding: '0.5rem',
                        border: '1px solid #3b82f6',
                        borderRadius: '4px',
                        fontSize: '0.875rem',
                        fontFamily: 'inherit',
                        background: 'white'
                      }}
                      autoFocus
                    />
                      <button
                        data-cy="save-core-value-btn"
                        onClick={() => {
                          // Apply the override
                          setIsEditingCoreValue(false);
                          setHasOverrides(true);
                          // Here you would typically call an API to save the override
                        }}
                        style={{
                          background: colors.primary,
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          padding: '0.5rem',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          transition: 'background 0.2s'
                        }}
                        onMouseOver={(e) => e.target.style.background = colors.primaryHover}
                        onMouseOut={(e) => e.target.style.background = colors.primary}
                        title="Save override"
                      >
                        <Save size={14} />
                      </button>
                      <button
                        data-cy="cancel-core-value-btn"
                        onClick={() => {
                          setEditedCoreValue(currentValues.master_value);
                          setIsEditingCoreValue(false);
                        }}
                        style={{
                          background: '#6b7280',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          padding: '0.5rem',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          transition: 'background 0.2s'
                        }}
                        onMouseOver={(e) => e.target.style.background = '#4b5563'}
                        onMouseOut={(e) => e.target.style.background = '#6b7280'}
                        title="Cancel edit"
                      >
                        <XIcon size={14} />
                      </button>
                  </div>
                ) : (
                  <ComparisonValue>{currentValues.master_value}</ComparisonValue>
                )}
                <ComparisonSource>System Reference</ComparisonSource>
              </ComparisonCard>
              </ComparisonGrid>
              
              {matchStatus === 'not_matching' && !showDocumentResults && (
                <div style={{ 
                  marginTop: '1.5rem',
                  padding: '1rem',
                  background: '#f8fafc',
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px'
                }}>
                  <div style={{ 
                    fontSize: '0.875rem', 
                    color: '#374151', 
                    marginBottom: '0.75rem',
                    lineHeight: '1.5'
                  }}>
                    <strong>Values don't match?</strong> Re-run the field extraction to see what was found in each document. This helps you understand why there's a mismatch and choose the correct value.
                  </div>
                  <button
                    onClick={handleReRunMatching}
                    disabled={isProcessing}
                    style={{
                      padding: '0.5rem 1rem',
                      background: colors.primary,
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      fontSize: '0.875rem',
                      fontWeight: '500',
                      cursor: isProcessing ? 'not-allowed' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      transition: 'background 0.2s',
                      opacity: isProcessing ? 0.6 : 1
                    }}
                    onMouseOver={(e) => {
                      if (!isProcessing) e.target.style.background = colors.primaryHover;
                    }}
                    onMouseOut={(e) => {
                      if (!isProcessing) e.target.style.background = colors.primary;
                    }}
                  >
                    <RefreshCw size={16} style={{ animation: isProcessing ? `${spin} 1s linear infinite` : 'none' }} />
                    {isProcessing ? 'Re-running...' : 'Re-run Field Extraction'}
                  </button>
                  
                  {/* WebSocket Progress Bar */}
                  {isProgressActive && (
                    <div style={{
                      marginTop: '1rem',
                      padding: '0.75rem',
                      background: '#f8fafc',
                      border: '1px solid #e2e8f0',
                      borderRadius: '6px'
                    }}>
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '0.5rem'
                      }}>
                        <span style={{
                          fontSize: '0.875rem',
                          color: '#374151',
                          fontWeight: '500'
                        }}>
                          {status}
                        </span>
                        <span style={{
                          fontSize: '0.875rem',
                          color: '#6b7280'
                        }}>
                          {progress}%
                        </span>
                      </div>
                      <div style={{
                        width: '100%',
                        height: '6px',
                        background: '#e5e7eb',
                        borderRadius: '3px',
                        overflow: 'hidden'
                      }}>
                        <div style={{
                          width: `${progress}%`,
                          height: '100%',
                          background: '#3b82f6',
                          borderRadius: '3px',
                          transition: 'width 0.3s ease'
                        }} />
                      </div>
                    </div>
                  )}
                </div>
              )}
            </ContentSection>

            {error && (
              <ErrorMessage>
                {error}
              </ErrorMessage>
            )}

            {showDocumentResults && (
              <FieldDocumentResults
                documentResults={documentResults}
                hoveredDocument={hoveredDocument}
                setHoveredDocument={setHoveredDocument}
                selectedDocument={selectedDocument}
                showDetailedView={showDetailedView}
                setShowDetailedView={setShowDetailedView}
                detailedDocumentIndex={detailedDocumentIndex}
                setDetailedDocumentIndex={setDetailedDocumentIndex}
                onValueSelect={(result, index) => {
                  setReplacedValue(result.value);
                  setReplacedSource(result.document);
                  setSelectedMatchType(result.matchType); // Capture the matchType from backend!
                  setHasOverrides(true);
                  setShowUndo(true);
                  setSelectedDocument(index);
                  
                  // The matchType from the backend will now be used by getDisplayMatchStatus()
                  // to show the correct match indicator (green for perfect, yellow for maybe, red for mismatch)
                  console.log(`[onValueSelect] Selected document with matchType: ${result.matchType}, value: ${result.value}`);
                }}
              />
            )}

            {!showDocumentResults && (
              <ContentSection>
                <ContentTitle>Comments</ContentTitle>
                <div style={{ marginBottom: '1rem' }}>
                  <textarea
                    value={comment}
                    onChange={(e) => {
                      setComment(e.target.value);
                      if (e.target.value.trim()) {
                        setHasOverrides(true); // Enable save button when comment is added
                      }
                    }}
                    placeholder="Add comments about this field match..."
                    style={{
                      width: '100%',
                      minHeight: '100px',
                      padding: '0.75rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '0.875rem',
                      fontFamily: 'inherit',
                      resize: 'vertical',
                      background: '#f9fafb'
                    }}
                  />
                </div>
              </ContentSection>
            )}
          </>
        );

      case 'overrides':
        return (
          <FieldOverridesSection
            error={error}
            createdBy={createdBy}
            setCreatedBy={setCreatedBy}
            overrideTypes={overrideTypes}
            onOverrideTypeChange={handleOverrideTypeChange}
            overrideValues={overrideValues}
            onValueChange={handleValueChange}
            comment={comment}
            setComment={setComment}
            renderStatusBar={renderStatusBar}
          />
        );

      case 'analysis':
        return (
          <FieldAnalysisSection
            isAnalyzing={isAnalyzing}
            analysisError={analysisError}
            analysisResult={analysisResult}
            onAnalyze={handleAnalyze}
            renderStatusBar={renderStatusBar}
          />
        );

      case 'history':
        return (
          <FieldHistorySection
            historyData={historyDataState}
            renderStatusBar={renderStatusBar}
            loading={loadingHistory}
          />
        );

      default:
        return null;
    }
  };

  return (
    <ModalPortal isOpen={isOpen}>
      <ModalOverlay data-cy="field-modal">
        <ModalContent>
        <FieldModalHeader
          fieldName={fieldName}
          loanId={loanId}
          borrowerName={borrowerName}
          fieldPriority={fieldPriority}
          onClose={onClose}
        />

        <ModalBody>
          {renderSidebar()}
          <MainContent>
            {renderMainContent()}
          </MainContent>
        </ModalBody>

        <BottomActions>
          <CancelButton onClick={onClose}>
            Cancel
          </CancelButton>
          <SaveButton data-cy="save-field-overrides" onClick={handleSave} disabled={loading || (!hasOverrides && !comment)}>
            <Save size={16} />
            {loading ? 'Saving...' : 'Save'}
          </SaveButton>
        </BottomActions>
      </ModalContent>
    </ModalOverlay>
    </ModalPortal>
  );
}

export default FieldModal;
